import { GraphQLErrors } from '@apollo/client/errors';
import { GraphQLError } from 'graphql';
import { GQLErrorCodeEnum } from 'src/app/shared/enums/graphql-error-code.enum';
import { GQLValidationErrorCodeEnum } from 'src/app/shared/enums/graphql-validation-error-code.enum';

/**
 * This set of utilities is intended for facilitating GraphQL error handling functionality.
 * The full scope is described in https://bettercaring.atlassian.net/browse/GE-486
 */

export type GraphQLErrorInput = GraphQLErrors | GraphQLError[] | { graphQLErrors: GraphQLError[] } | unknown;

/**
 * For destructing error object into array of GraphQL errors.
 *
 * @param errorObject - Error object (normally coming from catch block)
 * @returns - Array of GraphQLError
 */
export const getGraphQLErrorArrayFromObject = (errorObject: GraphQLErrorInput): GraphQLError[] => {
  if (Array.isArray(errorObject)) {
    return errorObject;
  }
  if (Array.isArray(errorObject?.['graphQLErrors'])) {
    return errorObject['graphQLErrors'];
  }
  return [];
};

/**
 * Helps to determine if the error returned from GraphQL is of a specific code.
 *
 * @param errorObject - Error object typically coming from the catch statement.
 * @param errorCode - Error code that the function is looking for from GQLErrorCodeEnum.
 * @returns - true if GraphQL code matches the error code provided.
 */
export const isGraphQLErrorCode = (errorObject: GraphQLErrorInput, errorCode: GQLErrorCodeEnum): boolean =>
  getGraphQLErrorArrayFromObject(errorObject).some((error) => error.extensions?.code === errorCode);

/**
 * Determine if the graphql validation error contains a specific error code.
 *
 * @param errorObject - Error object typically coming from the catch statement.
 * @param validationErrorCode - Validation error code from the GQLValidationErrorCodeEnum.
 * @returns - true if GraphQL validation code matches the error code provided.
 */
export const isGraphQLErrorValidationCode = (
  errorObject: GraphQLErrorInput,
  validationErrorCode: GQLValidationErrorCodeEnum,
): boolean =>
  getGraphQLErrorArrayFromObject(errorObject).some(
    (error) =>
      error.extensions?.code === GQLErrorCodeEnum.VALIDATION_ERROR &&
      error.extensions?.validation === validationErrorCode,
  );

/**
 * Returns Error Message Text by taking into account if the error message can be displayed to the user
 * (based on the display_message field).
 *
 * @param errorObject - The array that is returned when there is a graphQL error.
 */
export const getGraphQLErrorText = (errorObject: GraphQLError[] | { graphQLErrors: GraphQLError[] }): string => {
  const graphQLErrors: GraphQLError[] = getGraphQLErrorArrayFromObject(errorObject);

  // Segregating errors into the ones that should be visible to the end-user and not.
  // Only taking the first error to save up real estate on mobile screens.
  const error = graphQLErrors.find((error) => error.extensions?.display_message);

  if (error) {
    return error.message;
  }

  console.error('GraphQL Errors:', errorObject);
  return 'An error occurred.';
};

/**
 * Determine if the error is a timeout error.
 *
 * @param error - Error object
 * @returns - true if the error is a timeout error.
 */
export const isTimeoutError = (error: unknown): boolean => {
  // Check if error has a message property containing 'timeout'
  if (error?.['message']?.toLowerCase().includes('timeout')) {
    return true;
  }

  // Check if error is an array of errors containing timeout
  if (Array.isArray(error) && error.some((err) => err?.message?.toLowerCase().includes('timeout'))) {
    return true;
  }

  return false;
};
